import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import { Mail, Loader2, CheckCircle, AlertCircle, ArrowLeft, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { trpc } from "~/api/trpc";
import { Language, useLanguageStore } from "~/stores/language-store";
import { useCustomerAuthStore } from "~/stores/customer-auth-store";
import DE from "country-flag-icons/react/3x2/DE";
import GB from "country-flag-icons/react/3x2/GB";

const Login = () => {
    const { language, setLanguage } = useLanguageStore();
    const navigate = useNavigate();
    const { returnUrl, clearReturnUrl, token, customer } = useCustomerAuthStore();

    // The magic link is usually opened in another tab, which also clears returnUrl after logging in.
    // This tab picks up that login via the storage sync and returns to where the login was started.
    const returnUrlOnOpen = useRef(returnUrl);
    const isLoggedIn = !!token && !!customer;
    useEffect(() => {
        if (isLoggedIn) {
            navigate(returnUrlOnOpen.current || "/", { replace: true });
        }
    }, [isLoggedIn, navigate]);

    const [email, setEmail] = useState("");
    const [message, setMessage] = useState<string | null>(null);
    const [messageType, setMessageType] = useState<"success" | "error" | null>(null);

    const { data: cmsData } = trpc.publicBookingFunnel.getLoginConfig.useQuery(undefined, {
        staleTime: 60000,
    });
    const config = cmsData?.config;

    const requestMagicLinkMutation = trpc.publicCustomer.requestBookingWidgetMagicLinkEmail.useMutation();

    // CMS-driven localized text with fallbacks
    const title = language === Language.GERMAN
        ? (config?.titleDe || "Jetzt einloggen")
        : (config?.titleEn || "Sign In");
    const subtitle = language === Language.GERMAN
        ? (config?.subtitleDe || "Gib deine E-Mail-Adresse ein und erhalte deinen Login-Link.")
        : (config?.subtitleEn || "Enter your email address to receive your login link.");
    const emailPlaceholder = language === Language.GERMAN
        ? (config?.emailPlaceholderDe || "E-Mail-Adresse eingeben")
        : (config?.emailPlaceholderEn || "Enter your email address");
    const sendLinkButtonText = language === Language.GERMAN
        ? (config?.sendLinkButtonTextDe || "Link anfordern")
        : (config?.sendLinkButtonTextEn || "Send Link");
    const sendingText = language === Language.GERMAN
        ? (config?.sendingTextDe || "Wird gesendet...")
        : (config?.sendingTextEn || "Sending...");
    const successMessage = language === Language.GERMAN
        ? (config?.successMessageDe || "Prüfe deine E-Mails! Wir haben dir einen Login-Link gesendet.")
        : (config?.successMessageEn || "Check your email! We sent you a login link.");
    const notFoundError = language === Language.GERMAN
        ? (config?.notFoundErrorDe || "Kein Konto mit dieser E-Mail-Adresse gefunden.")
        : (config?.notFoundErrorEn || "No account found with this email address.");
    const genericError = language === Language.GERMAN
        ? (config?.genericErrorDe || "Ein Fehler ist aufgetreten. Bitte versuche es erneut.")
        : (config?.genericErrorEn || "Something went wrong. Please try again.");
    const backButtonText = language === Language.GERMAN
        ? (config?.backButtonTextDe || "Zurück zur Buchung")
        : (config?.backButtonTextEn || "Back to booking");

    const handleSubmit = async (e?: React.KeyboardEvent) => {
        if (e) {
            e.preventDefault();
        }

        if (!email.trim()) {
            return;
        }

        try {
            setMessage(null);
            setMessageType(null);

            await requestMagicLinkMutation.mutateAsync({ email: email.trim() });

            setMessage(successMessage);
            setMessageType("success");
            setEmail("");
        } catch (error: unknown) {
            const trpcError = error as { message?: string };
            if (trpcError.message?.includes('NOT_FOUND') || trpcError.message?.includes('No customer found')) {
                setMessage(notFoundError);
            } else {
                setMessage(genericError);
            }
            setMessageType("error");
        }
    };

    const handleBackToBooking = () => {
        const redirectTo = returnUrl || "/";
        clearReturnUrl();
        navigate(redirectTo);
    };

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    return (
        <motion.div
            className="flex flex-col min-h-screen bg-light-burgundy"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
        >
            {/* Header */}
            <div className="flex items-center justify-between p-4">
                <button
                    onClick={handleBackToBooking}
                    className="flex items-center gap-2 text-burgundy hover:text-burgundy/70 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="text-sm">{backButtonText}</span>
                </button>

                {/* Language Selector */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="flex items-center gap-1.5 px-2 h-8">
                            {language === Language.GERMAN ? (
                                <DE className="w-5 h-4" />
                            ) : (
                                <GB className="w-5 h-4" />
                            )}
                            <ChevronDown className="w-3 h-3 text-dark-gray" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem
                            onClick={() => setLanguage(Language.GERMAN)}
                            className="flex items-center gap-2 cursor-pointer"
                        >
                            <DE className="w-5 h-4" />
                            <span>Deutsch</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => setLanguage(Language.ENGLISH)}
                            className="flex items-center gap-2 cursor-pointer"
                        >
                            <GB className="w-5 h-4" />
                            <span>English</span>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 pb-20">
                <motion.div
                    className="w-full max-w-md"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
                >
                    {/* Card */}
                    <div className="bg-white rounded-lg p-6 shadow-md">
                        {/* Icon */}
                        <div className="flex justify-center mb-4">
                            <div className="w-12 h-12 border-2 border-burgundy rounded-full flex items-center justify-center">
                                <Mail className="w-6 h-6 text-burgundy" />
                            </div>
                        </div>

                        {/* Title */}
                        <h1 className="text-2xl font-semibold text-burgundy text-center mb-2">
                            {title}
                        </h1>
                        <p className="text-sm text-burgundy/70 text-center mb-6">
                            {subtitle}
                        </p>

                        {/* Form */}
                        <div className="space-y-4">
                            <Input
                                type="email"
                                placeholder={emailPlaceholder}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={requestMagicLinkMutation.isPending}
                                className="w-full h-12 border-burgundy/30 focus:border-burgundy focus-visible:ring-burgundy/20 text-burgundy placeholder:text-burgundy/50"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && isValidEmail && !requestMagicLinkMutation.isPending) {
                                        handleSubmit(e);
                                    }
                                }}
                            />

                            <button
                                onClick={() => handleSubmit()}
                                disabled={!isValidEmail || requestMagicLinkMutation.isPending}
                                className="w-full bg-burgundy text-white font-normal rounded-[5px] py-3 text-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-burgundy/90 transition-colors flex items-center justify-center gap-2"
                            >
                                {requestMagicLinkMutation.isPending ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        {sendingText}
                                    </>
                                ) : (
                                    sendLinkButtonText
                                )}
                            </button>
                        </div>

                        {/* Message */}
                        {message && (
                            <motion.div
                                className={`mt-4 p-4 rounded-lg flex items-start gap-3 ${
                                    messageType === "success"
                                        ? "bg-green-50 border border-green-200"
                                        : "bg-red-50 border border-red-200"
                                }`}
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                {messageType === "success" ? (
                                    <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                                ) : (
                                    <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                                )}
                                <p className={`text-sm ${
                                    messageType === "success" ? "text-green-800" : "text-red-800"
                                }`}>
                                    {message}
                                </p>
                            </motion.div>
                        )}
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default Login;
